import { take, put, all, takeEvery, PutEffect } from "redux-saga/effects";
import { getBranchId } from "../../../../helpers/commonHelpers";
import {
  bizBrandsDataCallComplete, catalogueDataCallComplete, discountsDataCallComplete,
  getBizBrandsData, getCatalogueData, getDiscountsData, getItemTag, getMostSoldItems,
  getOptionSetsData, getPOSSettingsData, itemTagCallComplete, mostSoldItemsCallComplete,
  optionSetsDataCallComplete, posSettingsCallComplete,
} from "../../../slices/catalogueSlice";
import { getItemsData, itemsDataCallComplete, setCatalogueRefetch } from "../../../slices/ItemsSlice";
import {
  businessDataCallComplete, getBusinessDataObj, getUserDataObj, userDataCallComplete,
} from "../../../slices/BusinessSlice";
import {
  allOutletsCallComplete, branchDataCallComplete, getAllOutlets,
} from "../../../slices/OutletStatusSlice";
import {
  allResourceGroupCallComplete, allResourcesCallComplete, allTablesCallComplete, fetchPrinter,
  getAllResourceGroup, getAllResources, getAllTables, getUsers, printersCallComplete,
  usersCallComplete,
} from "../../../slices/TableViewSlice";
import { needsMostSoldItemsFetch } from "../../../../helpers/catalogueHelpers";
import {
  fetchCompleteCatalogData, fetchCompleteCatalogDataSuccess, fetchOfflineUsers,
  fetchOfflineUsersCallComplete,
} from "../../../slices/LoginSlice";
import { getRecommenderConfig, recommenderConfigCallComplete } from "../../../slices/RecommenderSlice";
import { getReleaseNotesData, releaseNotesCallComplete } from "../../../slices/GlobalSlice";
import { IS_SUGGESTIVE_SELLING_V2_ENABLED } from "../../../../data/constants";

export * from "./shared";

/**
 * WEB variant of `redux-store/saga/login/fetchCatalog`. See `./shared.ts` — this module is
 * WEB-ONLY, so the native variant is deliberately empty.
 *
 * ⚠️ This is the post-login fan-out: it dispatches the whole catalogue/business/table set and
 * then WAITS on the matching `*CallComplete` actions before reporting success. The pairing of
 * each dispatch with its completion marker is the entire point of the file — the other platform
 * has no equivalent because it has no `*CallComplete` mechanism at all (which is the same
 * divergence recorded in `saga/pos/catalog` and `facade/ServiceFacade`).
 *
 * ⚠️ `IS_SUGGESTIVE_SELLING_V2_ENABLED` is a module-eval constant read from localStorage; see
 * `data/constants/index.desktop.ts`. Its value is fixed for the life of the page, exactly as
 * before the move.
 */

function* handleAfterLogin(action: any) {
  const { isRefetchingCatalog = false } = action.payload || {};
  const branchId = getBranchId();
  const hideLoader = false;

  const apiCalls: PutEffect<any>[] = [
    put(getPOSSettingsData({ branchId, hideLoader })),
    put(getCatalogueData()),
    put(getItemsData()),
    put(getOptionSetsData()),
    put(getUserDataObj({})),
    put(getAllOutlets()),
    put(getDiscountsData()),
    put(getAllTables()),
    put(getAllResources({})),
    put(getAllResourceGroup(hideLoader)),
    put(fetchPrinter()),
    put(getBizBrandsData()),
    put(getItemTag()),
    put(getUsers()),
    put(fetchOfflineUsers()),
    put(getBusinessDataObj({})),
    put(getReleaseNotesData({})),
  ];

  if (!IS_SUGGESTIVE_SELLING_V2_ENABLED) {
    apiCalls.push(put(getRecommenderConfig({})));
  }

  // Add conditionally
  let waitForMostSold = null;
  if (needsMostSoldItemsFetch()) {
    apiCalls.push(put(getMostSoldItems({ branchId })));
    waitForMostSold = take(mostSoldItemsCallComplete.type);
  }

  if (isRefetchingCatalog) {
    apiCalls.push(put(setCatalogueRefetch(true)));
  }

  // Dispatch all in parallel
  yield all(apiCalls);

  // Wait until all of them succeed
  const effects = [
    take(posSettingsCallComplete.type),
    take(catalogueDataCallComplete.type),
    take(itemsDataCallComplete.type),
    take(optionSetsDataCallComplete.type),
    take(userDataCallComplete.type),
    take(allOutletsCallComplete.type),
    take(discountsDataCallComplete.type),
    take(allTablesCallComplete.type),
    take(allResourcesCallComplete.type),
    take(allResourceGroupCallComplete.type),
    take(printersCallComplete.type),
    take(bizBrandsDataCallComplete.type),
    take(itemTagCallComplete.type),
    take(usersCallComplete.type),
    take(businessDataCallComplete.type),
    take(branchDataCallComplete.type),
    take(fetchOfflineUsersCallComplete.type),
    take(releaseNotesCallComplete.type)
  ];

  if (!IS_SUGGESTIVE_SELLING_V2_ENABLED) {
    effects.push(take(recommenderConfigCallComplete.type));
  }

  if (waitForMostSold) {
    effects.push(waitForMostSold);
  }

  yield all(effects);

  yield put(fetchCompleteCatalogDataSuccess());
}

// watcher
export default function* root(): any {
  yield takeEvery(fetchCompleteCatalogData.type, handleAfterLogin);
}
